import { BrowserModule } from '@angular/platform-browser';
import { NgModule, ErrorHandler, APP_INITIALIZER } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { RouterModule } from '@angular/router';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClient } from '@angular/common/http';

import { AppComponent } from './app.component';
import { OlMapComponent } from './ol-map/ol-map.component';
import { LayersTreeComponent } from './layers-tree/layers-tree.component';
import { MapViewComponent } from './map-view/map-view.component';
import { AppMenuComponent } from './app-menu/app-menu.component';
import { FeatureInfoComponent } from './feature-info/feature-info.component';
import { LoginComponent } from './login/login.component';
import { TableComponent } from './table/table.component';
import { SearchComponent } from './search/search.component';
import { SelectedLayersDetailViewComponent } from './selected-layers-detail-view/selected-layers-detail-view.component';
import { AdminComponent } from './admin/admin.component';
import { ReportsComponent } from './reports/reports.component';
import { GraphviewComponent } from './graphview/graphview.component';
import { DoPlanningComponent } from './do-planning/do-planning.component';
import { RemotePowerComponent } from './do-planning/remote-power/remote-power.component';
import { RemotePowerDetailsComponent } from './do-planning/remote-power-details/remote-power-details.component';
import { RemotePowerDetailsTableComponent } from './do-planning/remote-power-details-table/remote-power-details-table.component';
import { SolarReceiverComponent } from './do-planning/solar-receiver/solar-receiver.component';

import { FeaturePipe } from './feature-info/feature.pipe';
import { MapnameDirective } from './ol-map/mapname.directive';
import { HeaderParserPipe } from './pipes/header-parser.pipe';
import { OpisFormatPipe } from './pipes/opis-format.pipe';

import { AngularSplitModule } from 'angular-split';

// PrimeNG
import { providePrimeNG } from 'primeng/config';
import { TreeModule } from 'primeng/tree';
import { MenubarModule } from 'primeng/menubar';
import { DataViewModule } from 'primeng/dataview';
import { DialogModule } from 'primeng/dialog';
import { PopoverModule } from 'primeng/popover';
import { AccordionModule } from 'primeng/accordion';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { TabsModule } from 'primeng/tabs';
import { SelectModule } from 'primeng/select';
import { SliderModule } from 'primeng/slider';
import { PanelModule } from 'primeng/panel';
import { FieldsetModule } from 'primeng/fieldset';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { SelectButtonModule } from 'primeng/selectbutton';
import { CheckboxModule } from 'primeng/checkbox';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { FileUploadModule } from 'primeng/fileupload';
import { DragDropModule } from 'primeng/dragdrop';
import { PickListModule } from 'primeng/picklist';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ProgressBarModule } from 'primeng/progressbar';
import { ChartModule } from 'primeng/chart';
import { DatePickerModule } from 'primeng/datepicker';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SplitButtonModule } from 'primeng/splitbutton';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';

import { ConfirmationService, MessageService } from 'primeng/api';

// Routes
import { appRoutes } from './_routes/app.routes';

// Services
import { EventsService } from './_services/events.service';
import { LayersService } from './_services/layers.service';
import { TabledataService } from './_services/tabledata.service';
import { SearchService } from './search/search.service';
import { GlobalsService } from './_services/globals.service';
import { LoginService } from './login/login.service';
import { AdminService } from './_services/admin.service';
import { ReportsService } from './reports/reports.service';
import { GraphviewService } from './graphview/graphview.service';
import { HeaderService } from './_services/header.service';
import { RemotePowerService } from './_services/remote-power.service';

import { LoginGuard } from './login/login-guard';
import { KeycloakService } from './login/keycloak.service';
import { keycloakHttpFactory, KeycloakHttpService } from './login/keycloak-http.service';

import { ElekErrorHandler } from './elek-error-handler';
import { Mapapi } from './ol-map/mapapi';

import { SagaBlueLike } from './theme';

import { LOCALE_ID } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localeSl from '@angular/common/locales/sl';

import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { AuthInterceptor } from './login/auth.interceptor';

import { ForbiddenInterceptor } from './forbidden.interceptor';

export function initApp(globalsService: GlobalsService) {
  return () =>
    new Promise<void>((resolve, reject) => {
      globalsService.initConfig().subscribe({
        next: (ok) => {
          if (ok) {
            resolve();
          } else {
            reject(new Error('Failed to load settings.json'));
          }
        },
        error: (err) => reject(err),
      });
    });
}
registerLocaleData(localeSl);

@NgModule({
  declarations: [
    AppComponent,
    OlMapComponent,
    LayersTreeComponent,
    MapViewComponent,
    AppMenuComponent,
    FeatureInfoComponent,
    LoginComponent,
    FeaturePipe,
    TableComponent,
    MapnameDirective,
    SearchComponent,
    SelectedLayersDetailViewComponent,
    AdminComponent,
    ReportsComponent,
    GraphviewComponent,
    DoPlanningComponent,
    RemotePowerComponent,
    RemotePowerDetailsComponent,
    RemotePowerDetailsTableComponent,
    HeaderParserPipe,
    SolarReceiverComponent,
    OpisFormatPipe,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule.forRoot(appRoutes, { enableTracing: false }),
    AngularSplitModule,
    
    ConfirmDialogModule,
    TreeModule,
    DataViewModule,
    PopoverModule,
    MenubarModule,
    DialogModule,
    AccordionModule,
    TabsModule,
    SelectModule,
    ToggleSwitchModule,
    ToggleButtonModule,
    SplitButtonModule,
    DatePickerModule,
    SliderModule,
    PanelModule,
    RadioButtonModule,
    FieldsetModule,
    AutoCompleteModule,
    SelectButtonModule,
    CheckboxModule,
    ButtonModule,
    TooltipModule,
    FileUploadModule,
    DragDropModule,
    PickListModule,
    ProgressBarModule,
    ChartModule,
    TableModule,
    ToastModule,
  ],
  providers: [
    providePrimeNG({
      ripple: false,
      theme: {
        preset: SagaBlueLike,
        options: { cssLayer: false, darkModeSelector: false },
      },
    }),

    { provide: LOCALE_ID, useValue: 'sl-SI' },

    {
      provide: APP_INITIALIZER,
      useFactory: initApp,
      deps: [GlobalsService],
      multi: true,
    },

    {
      provide: KeycloakHttpService,
      useFactory: keycloakHttpFactory,
      deps: [HttpClient, KeycloakService],
      multi: true,
    },

    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ForbiddenInterceptor,
      multi: true
    },

    KeycloakService,
    RemotePowerService,
    HeaderService,
    GraphviewService,
    ReportsService,
    LayersService,
    AdminService,
    EventsService,
    Mapapi,
    TabledataService,
    SearchService,
    GlobalsService,
    LoginService,
    LoginGuard,
    ConfirmationService,
    MessageService,

    { provide: ErrorHandler, useClass: ElekErrorHandler },

    provideHttpClient(withInterceptorsFromDi()),
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}